/*
 * Size guide drawer
 *
 * Abre `#SizeGuideDrawer` desde cualquier `[data-size-guide-open]` (el botón
 * "Guía de talles" del variant picker) y replica el resaltado en cruz de la
 * tabla: al apuntar un valor se marca su fila completa y la columna a la que
 * pertenece, con la primera columna y la celda apuntada en el tono oscuro.
 *
 * La sección sólo se imprime si el producto tiene un tag que coincide con
 * alguna tabla; cuando no hay drawer en la página, el botón que lo abriría se
 * oculta en vez de quedar como un click muerto.
 */
(() => {
  const TRIGGER = '[data-size-guide-open]';
  const CLOSER = '[data-size-guide-close]';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const HISTORY_ID = 'size-guide-drawer';

  let lastTrigger = null;

  const drawerEl = () => document.getElementById('SizeGuideDrawer');
  const isOpen = (drawer) => drawer.getAttribute('aria-hidden') === 'false';

  const open = (trigger) => {
    const drawer = drawerEl();
    if (!drawer || isOpen(drawer)) return;

    const panel = drawer.querySelector('.size-guide__panel');
    const body = drawer.querySelector('.size-guide__body');
    if (body) body.scrollTop = 0;

    lastTrigger = trigger || null;
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('size-guide-open');
    if (panel) panel.focus({ preventScroll: true });

    // Ver `ig-overlay-history.js`: el gesto de volver del telefono cierra el
    // panel en vez de salir de la ficha.
    if (window.IGOverlayHistory) {
      window.IGOverlayHistory.push(HISTORY_ID, () => close({ fromHistory: true }));
    }
  };

  const close = (options) => {
    const drawer = drawerEl();
    if (!drawer || !isOpen(drawer)) return;

    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('size-guide-open');
    if (window.IGOverlayHistory && !(options && options.fromHistory)) {
      window.IGOverlayHistory.pop(HISTORY_ID);
    }

    if (lastTrigger) {
      const target = lastTrigger;
      lastTrigger = null;
      if (typeof target.focus === 'function') target.focus({ preventScroll: true });
    }
  };

  const trapTab = (event, panel) => {
    const items = Array.prototype.filter.call(
      panel.querySelectorAll(FOCUSABLE),
      (node) => node.offsetParent !== null || node === document.activeElement
    );
    if (!items.length) {
      event.preventDefault();
      panel.focus({ preventScroll: true });
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* --- Resaltado en cruz --------------------------------------------------- */

  const clearHighlight = (columns) => {
    columns.forEach((column) => {
      Array.prototype.forEach.call(column.children, (cell) => {
        cell.classList.remove('active', 'active-dark');
      });
    });
  };

  const highlight = (cell, columns) => {
    const column = cell.parentElement;
    const rowIndex = Array.prototype.indexOf.call(column.children, cell);
    const columnIndex = Number(column.dataset.index);

    // columna apuntada: todo lo que hay por encima de la celda
    for (let i = 0; i < rowIndex; i += 1) {
      column.children[i].classList.add(i === 0 ? 'active-dark' : 'active');
    }

    // fila apuntada: oscura en la primera columna y en la columna apuntada
    columns.forEach((sibling, i) => {
      const rowCell = sibling.children[rowIndex];
      if (!rowCell) return;
      rowCell.classList.add(i === 0 || i === columnIndex ? 'active-dark' : 'active');
    });
  };

  const bindHighlight = (table) => {
    if (table.dataset.sizeGuideBound === 'true') return;
    table.dataset.sizeGuideBound = 'true';

    const columns = Array.prototype.slice.call(table.querySelectorAll('.column'));
    if (!columns.length) return;

    columns.forEach((column) => {
      Array.prototype.forEach.call(column.children, (cell) => {
        if (!cell.classList.contains('column-value')) return;
        cell.addEventListener('mouseenter', () => highlight(cell, columns));
        cell.addEventListener('mouseleave', () => clearHighlight(columns));
      });
    });
  };

  /* --- Arranque ------------------------------------------------------------ */

  const sync = () => {
    const drawer = drawerEl();
    const hasTables = Boolean(drawer && drawer.querySelector('.size-guide__table'));

    document.querySelectorAll(TRIGGER).forEach((trigger) => {
      const holder = trigger.closest('.product__size-guide') || trigger;
      holder.hidden = !hasTables;
    });

    if (!drawer) return;
    drawer.querySelectorAll('.size-guide__table').forEach(bindHighlight);
  };

  const init = () => {
    sync();

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (target.closest(CLOSER)) {
        event.preventDefault();
        close();
        return;
      }

      const trigger = target.closest(TRIGGER);
      if (!trigger) return;
      event.preventDefault();
      open(trigger);
    });

    document.addEventListener('keydown', (event) => {
      const drawer = drawerEl();
      if (!drawer || !isOpen(drawer)) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key === 'Tab') {
        const panel = drawer.querySelector('.size-guide__panel');
        if (panel) trapTab(event, panel);
      }
    });

    // el editor de temas re-renderiza secciones sin recargar la página
    document.addEventListener('shopify:section:load', sync);
    document.addEventListener('shopify:section:unload', sync);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
